import { useEffect, useState } from "react";
import Image from "next/image";
import AbsenModal from "@/components/elements/KonfirmasiLokasiModal";
import Swal from "sweetalert2";
import PhotoCamera from "@/components/elements/PhotoCamera";
import { useAuth } from "@/context/AuthContext";

interface AbsenProps {
  date: string;
  is_absen_in: number | undefined;
  is_absen_out: number | undefined;
}

const Absen: React.FC<AbsenProps> = ({ date, is_absen_in, is_absen_out }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [showPhotoCamera, setShowPhotoCamera] = useState(false);
  const [photoMode, setPhotoMode] = useState<"dalam" | "luar" | null>(null);
  const [gps_lat, setGpsLat] = useState<number>(0);
  const [gps_long, setGpsLong] = useState<number>(0);
  const [isMasuk, setIsMasuk] = useState<number>(0);
  const [isKantor, setIsKantor] = useState<number>(0);
  const [isCheckingDistance, setIsCheckingDistance] = useState<boolean>(false);

  const { user } = useAuth();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleCheckAbsen = async (is_masuk: number) => {
    setIsMasuk(is_masuk);
    const today = new Date().toISOString().split("T")[0];

    try {
      const response = await fetch(
        "https://tarum-tools.aurorasystem.co.id/kepegawaian-api/check_absen_v2.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tgl: today,
            pgw_id: Number(user?.pgw_id),
            is_masuk,
            is_location: isKantor,
          }),
        }
      );

      const result = await response.json();

      switch (result.stts) {
        case "0":
          setModalOpen(true); // Membuka AbsenModal
          break;
        case "98":
          setModalOpen(true); // Luar kantor lanjutbisa buka modal
          break;
        case "1":
          Swal.fire("Info", "Pegawai sudah Absen.", "info");
          break;
        case "99":
          Swal.fire("Warning", "Pegawai tidak diizinkan absen.", "warning");
          break;
        default:
          Swal.fire("Error", "Terjadi kesalahan. Silakan coba lagi.", "error");
      }
    } catch (error) {
      console.error("Error checking absen:", error);
      Swal.fire("Error", "Terjadi kesalahan koneksi.", "error");
    }
  };

  const handleSelectOption = async (type: "dalam" | "luar") => {
    if (type === "luar") {
      const today = new Date().toISOString().split("T")[0];

      try {
        const response = await fetch(
          "https://tarum-tools.aurorasystem.co.id/kepegawaian-api/check_absen_v2.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              tgl: today,
              pgw_id: Number(user?.pgw_id),
              is_masuk: isMasuk, // Menggunakan state isMasuk yang sudah diset di awal
              is_location: 0,    // 0 menandakan luar kantor
            }),
          }
        );

        const result = await response.json();

        if (result.status === "403" || result.error === "CANNOT_ATTEDANCE_OUTSIDE") {
          Swal.fire(
            "Ditolak",
            result.keterangan || "Tidak diizinkan absen diluar kantor!",
            "error"
          );
          return;
        }

        setIsKantor(0);
        setPhotoMode("luar");
        setModalOpen(false);
        setShowPhotoCamera(true);

      } catch (error) {
        console.error("Error checking absen luar kantor:", error);
        Swal.fire(
          "Error",
          "Terjadi kesalahan koneksi saat memverifikasi status luar kantor.",
          "error"
        );
      }
      return;
    }

    if (!navigator.geolocation) {
      alert("Geolocation tidak didukung oleh browser ini.");
      return;
    }

    setIsCheckingDistance(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const deviceLatitude = position.coords.latitude;
        const deviceLongitude = position.coords.longitude;

        setGpsLat(deviceLatitude);
        setGpsLong(deviceLongitude);

        const of_id = user?.off_id ? Number(user.off_id) : null;
        const pgw_id = user?.pgw_id ? Number(user.pgw_id) : null;

        if (of_id !== null && pgw_id !== null) {
          handleDistanceCheck(deviceLatitude, deviceLongitude, of_id, pgw_id);
        } else {
          setIsCheckingDistance(false);
          Swal.fire("Error", "ID pegawai/kantor tidak ditemukan.", "error");
        }
      },
      (error) => {
        console.error("Error getting location:", error);
        setIsCheckingDistance(false);
        Swal.fire("Error", "Gagal mendapatkan lokasi perangkat.", "error");
      }
    );
  };

  const handleDistanceCheck = async (
    latitude: number,
    longitude: number,
    of_id: number,
    pgw_id: number
  ) => {
    try {
      const response = await fetch(
        "https://tarum-tools.aurorasystem.co.id/kepegawaian-api/check_distance_absensi.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            gps_lat: latitude,
            gps_long: longitude,
            of_id,
            pgw_id,
          }),
        }
      );

      const result = await response.json();

      if (result.valid === 1) {
        // Cuma di titik INI is_kantor boleh jadi 1 dan kamera boleh kebuka.
        Swal.fire("Success", "Anda berada dalam jangkauan kantor.", "success");
        setIsKantor(1);
        setPhotoMode("dalam");
        setShowPhotoCamera(true);
        setModalOpen(false);
      } else {
        // Invalid -> is_kantor TETEP 0, kamera TETEP ga kebuka.
        setIsKantor(0);
        setShowPhotoCamera(false);
        Swal.fire(
          "Error",
          "Lokasi tidak valid. Anda tidak dapat absen di kantor.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error checking distance:", error);
      setIsKantor(0);
      setShowPhotoCamera(false);
      Swal.fire(
        "Error",
        "Terjadi kesalahan koneksi. Silakan coba lagi.",
        "error"
      );
    } finally {
      setIsCheckingDistance(false);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", { hour12: false });
  };

  const dateObj = new Date();
  const formattedDate = dateObj.toLocaleDateString("id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="flex items-center justify-center -mt-28 sm:mt-0">
      <div className="bg-white shadow-md rounded-xl p-4 w-full max-w-md">
        <div className="mb-6 flex justify-around items-center">
          <div className="text-sm sm:text-lg font-bold">
            <p>{formattedDate.split(",")[0]},</p>
            <p>{formattedDate.split(",")[1]}</p>
          </div>
          <div className="flex items-center space-x-2">
            {formatTime(currentTime)
              .split(":")
              .map((unit, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg bg-gray-50">
                    <span className="text-md sm:text-lg font-normal">
                      {unit}
                    </span>
                  </div>
                  {index < 2 && (
                    <span className="text-xl sm:text-lg font-bold mx-1">:</span>
                  )}
                </div>
              ))}
          </div>
        </div>

        <div className="flex justify-around">
          <div className="flex flex-col items-center space-y-4">
            <Image
              src="/ic_absen_masuk.svg"
              alt="Absen Masuk"
              width={120}
              height={64}
            />
            {is_absen_in ? (
              <button
                className="bg-[#6FCF9740] text-[#219653] px-8 py-4 text-[11px] sm:text-sm font-semibold rounded-full mt-2 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled
              >
                ✅ Sudah Absen
              </button>
            ) : (
              <button
                className="bg-primary text-white hover:bg-red-600 px-8 py-4 text-[11px] sm:text-sm font-semibold rounded-full mt-2 transition-colors duration-300"
                onClick={() => handleCheckAbsen(1)}
              >
                Absen Masuk
              </button>
            )}
          </div>

          <div className="flex flex-col items-center space-y-4">
            <Image
              src="/ic_absen_keluar.svg"
              alt="Absen Keluar"
              width={120}
              height={64}
            />
            {is_absen_out ? (
              <button
                className="bg-[#6FCF9740] text-[#219653] px-8 py-4 text-[11px] sm:text-sm font-semibold rounded-full mt-2 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled
              >
                ✅ Sudah Absen
              </button>
            ) : (
              <button
                className="bg-muted text-textGray text-[11px] sm:text-sm font-semibold px-8 py-4 rounded-full mt-2 transition-colors duration-300"
                onClick={() => handleCheckAbsen(0)}
              >
                Absen Pulang
              </button>
            )}
          </div>
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg p-6 shadow-lg max-w-sm text-center">
              <p className="text-lg font-bold mb-4">Lokasi Valid</p>
              <p className="text-sm text-gray-600 mb-6">
                Anda berada dalam jangkauan kantor dan dapat melakukan absen.
              </p>
              <button
                className="bg-primary text-white px-6 py-2 rounded-lg"
                onClick={() => setIsModalOpen(false)}
              >
                OK
              </button>
            </div>
          </div>
        )}

        <AbsenModal
          isOpen={modalOpen}
          jenis={isMasuk == 1 ? "Masuk" : "Pulang"}
          onClose={() => setModalOpen(false)}
          onSelect={handleSelectOption}
        />

        {isCheckingDistance && (
          <p className="text-center text-sm text-gray-500 mt-2">
            Memeriksa lokasi...
          </p>
        )}

        {showPhotoCamera && (
          <PhotoCamera
            mode={photoMode}
            pgw_id={user?.pgw_id ?? ""}
            gps_lat={gps_lat}
            gps_long={gps_long}
            is_kantor={isKantor}
            is_masuk={isMasuk}
            tipe_absen={1}
          />
        )}
      </div>
    </div>
  );
};

export default Absen;